import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSeo } from '@/hooks/useSeo';

export default function Article() {
  const { t, i18n } = useTranslation();
  useSeo();

  const sections = Array.from({ length: 7 }, (_, i) => i + 1);
  const faqItems = Array.from({ length: 6 }, (_, i) => i + 1);
  const tableRows = Array.from({ length: 5 }, (_, i) => i + 1);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('article.h1'),
    description: t('article.metaDescription'),
    datePublished: '2026-01-15',
    dateModified: '2026-08-20',
    inLanguage: i18n.language,
    author: { '@type': 'Organization', name: 'USFans Spreadsheet Resource Hub' },
    publisher: { '@type': 'Organization', name: 'USFans Spreadsheet Resource Hub' },
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-16">
          <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="transition-colors hover:text-foreground-900 cursor-pointer">
                  {t('article.breadcrumbHome')}
                </Link>
              </li>
              <li className="flex items-center gap-1.5">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-s-line text-base leading-none"></i>
                </span>
                <span className="text-foreground-700">{t('article.breadcrumbCurrent')}</span>
              </li>
            </ol>
          </nav>

          <div className="mt-6 flex items-center gap-2">
            <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800 whitespace-nowrap">
              {t('article.category')}
            </span>
            <span className="text-xs text-foreground-500">{t('article.readingTime')}</span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-4xl">
            {t('article.h1')}
          </h1>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-foreground-500">
            <span>{t('article.published')}</span>
            <span>{t('article.updated')}</span>
          </div>

          <p className="mt-6 text-base leading-relaxed text-foreground-700 md:text-lg">
            {t('article.intro')}
          </p>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-foreground-900">
              {t('article.tocTitle')}
            </h2>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {sections.map((i) => (
                <li key={i}>
                  <a
                    href={`#section-${i}`}
                    className="text-sm text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer"
                  >
                    {t(`article.s${i}.title`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {sections.map((i) => (
            <section key={i} id={`section-${i}`} className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
                {t(`article.s${i}.title`)}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">
                {t(`article.s${i}.body1`)}
              </p>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">
                {t(`article.s${i}.body2`)}
              </p>
            </section>
          ))}

          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
              {t('article.tableTitle')}
            </h2>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[560px] border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-primary-50 text-foreground-900">
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">
                      {t('article.table.c1')}
                    </th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">
                      {t('article.table.c2')}
                    </th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">
                      {t('article.table.c3')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((r) => (
                    <tr key={r} className="border-t border-background-200 odd:bg-background-50 even:bg-background-100">
                      <td className="px-4 py-3 font-semibold text-foreground-900">
                        {t(`article.table.r${r}c1`)}
                      </td>
                      <td className="px-4 py-3 text-foreground-700">
                        {t(`article.table.r${r}c2`)}
                      </td>
                      <td className="px-4 py-3 text-foreground-700">
                        {t(`article.table.r${r}c3`)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-heading text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
              {t('article.faqTitle')}
            </h2>
            <div className="mt-5 space-y-4">
              {faqItems.map((i) => (
                <div key={i}>
                  <h3 className="text-base font-semibold text-foreground-900">
                    {t(`article.fq${i}`)}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    {t(`article.fa${i}`)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 rounded-lg border border-background-200 bg-secondary-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {t('article.relatedTitle')}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              {t('article.relatedDesc')}
            </p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-secondary-700 hover:text-secondary-800 cursor-pointer"
            >
              {t('cta.backHome')}
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line text-base leading-none"></i>
              </span>
            </Link>
          </div>

          <div className="mt-8 text-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-8 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t('article.cta')}
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-external-link-line text-base leading-none"></i>
              </span>
            </a>
          </div>

          <p className="mt-10 rounded-lg border border-background-200 bg-background-100 p-4 text-xs leading-relaxed text-foreground-500">
            {t('article.disclaimer')}
          </p>
        </article>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </>
  );
}